import { memo, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DataSource } from "../types"

import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SortableTableHeader } from "@/components/ui/sortable-table-header"
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table"

import { EmptyTableRow } from "./admin-dashboard-empty"
import { getEmptyStates } from "./admin-dashboard-empty.utils"
import { DataSourceIcon, StatusBadge } from "./admin-dashboard-shared"
import { MobileSortControls } from "./mobile-sort-controls"

export const DataSourcesTable = memo(function DataSourcesTable({
  sources,
}: {
  sources: DataSource[] | null | undefined
}) {
  const { t } = useTranslation()
  const EMPTY_STATES = getEmptyStates(t)
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("all")
  const filteredSources = useMemo(
    () =>
      (sources ?? []).filter(
        (source) =>
          `${source.name} ${source.type}`
            .toLocaleLowerCase()
            .includes(search.trim().toLocaleLowerCase()) &&
          (status === "all" || source.status === status)
      ),
    [sources, search, status]
  )
  const [sort, setSort] = useState<{ key: string | null; direction: "asc" | "desc" | null }>({
    key: null,
    direction: null,
  })

  const sortedSources = useMemo(() => {
    if (!sort.key || !sort.direction) {
      return filteredSources
    }

    const sorted = [...filteredSources].sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sort.key) {
        case "source":
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case "status":
          aValue = a.status.toLowerCase()
          bValue = b.status.toLowerCase()
          break
        case "items":
          aValue = a.items
          bValue = b.items
          break
        case "lastSync":
          // Parse date strings for comparison (assuming ISO format or similar)
          aValue = new Date(a.lastSync).getTime()
          bValue = new Date(b.lastSync).getTime()
          if (!Number.isFinite(aValue)) return Number.isFinite(bValue) ? 1 : 0
          if (!Number.isFinite(bValue)) return -1
          break
        default:
          return 0
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return sort.direction === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number)
    })

    return sorted
  }, [filteredSources, sort])

  const handleSort = (key: string) => {
    setSort((prev) => {
      if (prev.key === key) {
        // Cycle through: asc -> desc -> null -> asc
        if (prev.direction === "asc") {
          return { key, direction: "desc" }
        }
        if (prev.direction === "desc") {
          return { key: null, direction: null }
        }
        return { key, direction: "asc" }
      }
      return { key, direction: "asc" }
    })
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <Input
          aria-label={t("adminDashboard.tableControls.search", "Search rows")}
          placeholder={t("adminDashboard.tableControls.search", "Search rows")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            aria-label={t("adminDashboard.tableControls.status", "Filter by status")}
            className="w-full sm:w-40"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("adminDashboard.tableControls.all", "All statuses")}
            </SelectItem>
            {Array.from(new Set((sources ?? []).map((source) => source.status)))
              .sort()
              .map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2 lg:hidden">
        {sortedSources.length > 0 ? (
          <MobileSortControls
            ariaLabel="Sort data sources"
            direction={sort.direction}
            sortKey={sort.key}
            options={[
              { id: "source", label: t("adminDashboard.tables.dataSources.headers.source") },
              { id: "status", label: t("adminDashboard.tables.dataSources.headers.status") },
              { id: "items", label: t("adminDashboard.tables.dataSources.headers.items") },
              {
                id: "lastSync",
                label: t("adminDashboard.tables.dataSources.headers.lastSync"),
              },
            ]}
            onSortChange={(key, direction) => setSort({ key, direction })}
          />
        ) : null}
        {sortedSources.length === 0 ? (
          <p className="text-muted-foreground rounded-lg border p-6 text-center text-sm">
            {EMPTY_STATES.dataSources.title}
          </p>
        ) : (
          sortedSources.map((source, index) => (
            <div
              key={`${source.name}-mobile-${index}`}
              className="bg-card space-y-3 rounded-lg border p-3 text-sm"
              data-testid="data-source-mobile-row"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-md">
                  <DataSourceIcon type={source.type} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{source.name}</div>
                  <div className="text-muted-foreground text-xs">{source.type}</div>
                </div>
                <StatusBadge status={source.status} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-muted-foreground block">Items</span>
                  <span className="font-medium tabular-nums">{source.items.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block">Last synced</span>
                  <span className="font-medium">{source.lastSync}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <Table className="min-w-[560px]">
          <TableHeader>
            <TableRow>
              <SortableTableHeader
                align="left"
                currentSort={sort}
                sortKey="source"
                onSort={handleSort}
              >
                {t("adminDashboard.tables.dataSources.headers.source")}
              </SortableTableHeader>
              <SortableTableHeader
                align="left"
                currentSort={sort}
                sortKey="status"
                onSort={handleSort}
              >
                {t("adminDashboard.tables.dataSources.headers.status")}
              </SortableTableHeader>
              <SortableTableHeader
                align="left"
                currentSort={sort}
                sortKey="items"
                onSort={handleSort}
              >
                {t("adminDashboard.tables.dataSources.headers.items")}
              </SortableTableHeader>
              <SortableTableHeader
                align="left"
                currentSort={sort}
                sortKey="lastSync"
                onSort={handleSort}
              >
                {t("adminDashboard.tables.dataSources.headers.lastSync")}
              </SortableTableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedSources.length === 0 ? (
              <EmptyTableRow colSpan={4} message={EMPTY_STATES.dataSources.title} />
            ) : (
              sortedSources.map((source, index) => (
                <TableRow key={`${source.name}-${index}`}>
                  <TableCell className="min-w-48 pr-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="bg-muted shrink-0 rounded-lg p-2">
                        <DataSourceIcon type={source.type} />
                      </div>
                      <div className="min-w-0 font-medium break-words">{source.name}</div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <StatusBadge status={source.status} />
                  </TableCell>
                  <TableCell>{source.items.toLocaleString()}</TableCell>
                  <TableCell className="whitespace-nowrap">{source.lastSync}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
})
